import { apiClient } from './client';

/**
 * board-module-v1 API client — 4 도메인 (Notice / Post / FAQ / Inquiry).
 *
 * backend: apps/chat-server/src/modules/board/board.routes.ts
 *
 * 정책:
 *   - 모든 endpoint 인증 필수 (apiClient 가 cookie 자동 — auth v1.5 cutover)
 *   - viewCount 자동 increment (GET detail 시)
 *   - inquiry 권한: 본인 또는 ADMIN (existence leak 차단 → 404)
 */

// ─── Notice ────────────────────────────────────────────────

export interface NoticeAuthor {
  id: string;
  name: string;
}

export interface NoticeListItem {
  id: string;
  title: string;
  isImportant: boolean;
  viewCount: number;
  postedAt: string;
  author: NoticeAuthor;
  /*
   * 읽음 · 확인 (2026-09-16) — **서버는 이 셋을 이미 보내고 있었다**
   * (`board-notice.routes.ts` 의 목록 응답 — 2026-09-14 R4-06 에서 더해졌다).
   * 여기에 칸이 없어서 **타입이 가리고 있었을 뿐이다.**
   *
   * ★ 셋 다 **선택**이다 — 옛 서버는 안 보낸다. 안 오면 «모르는 것»이고,
   *   모를 때는 **읽은 것처럼 흐리게 하지 않는다**(새 공지를 지워 버리는 쪽이 더 나쁘다).
   */
  isRead?: boolean;
  /** 확인이 필요한 공지에서 **확인을 누른** 시각. 읽기만 한 것과 다르다. */
  confirmedAt?: string | null;
  /** 이 공지가 「확인」까지 받아야 하는가. */
  requiresConfirm?: boolean;
}

export interface NoticeDetail extends NoticeListItem {
  content: string;
  contentType: 'HTML' | 'TEXT';
}

export interface CreateNoticeInput {
  title: string;
  content: string;
  isImportant?: boolean;
  contentType?: 'HTML' | 'TEXT';
}

export async function listNotices(importantOnly = false): Promise<NoticeListItem[]> {
  const { data } = await apiClient.get<{ notices: NoticeListItem[] }>('/board/notices', {
    params: importantOnly ? { important: 'true' } : undefined,
  });
  return Array.isArray(data?.notices) ? data.notices : [];
}

export async function getNotice(id: string): Promise<NoticeDetail> {
  const { data } = await apiClient.get<{ notice: NoticeDetail }>(`/board/notices/${id}`);
  return data.notice;
}

export async function createNotice(input: CreateNoticeInput): Promise<NoticeDetail> {
  const { data } = await apiClient.post<{ notice: NoticeDetail }>('/board/notices', input);
  return data.notice;
}

// ─── Post ──────────────────────────────────────────────────

export type PostCategory = 'free' | 'tech' | 'info';

export interface PostListItem {
  id: string;
  title: string;
  category: PostCategory;
  viewCount: number;
  createdAt: string;
  author: NoticeAuthor;
}

export interface PostDetail extends PostListItem {
  content: string;
  contentType: 'HTML' | 'TEXT';
}

export interface CreatePostInput {
  title: string;
  content: string;
  category?: PostCategory;
  contentType?: 'HTML' | 'TEXT';
}

export async function listPosts(category?: PostCategory): Promise<PostListItem[]> {
  const { data } = await apiClient.get<{ posts: PostListItem[] }>('/board/posts', {
    params: category ? { category } : undefined,
  });
  return Array.isArray(data?.posts) ? data.posts : [];
}

export async function getPost(id: string): Promise<PostDetail> {
  const { data } = await apiClient.get<{ post: PostDetail }>(`/board/posts/${id}`);
  return data.post;
}

export async function createPost(input: CreatePostInput): Promise<PostDetail> {
  const { data } = await apiClient.post<{ post: PostDetail }>('/board/posts', input);
  return data.post;
}

export async function deletePost(id: string): Promise<void> {
  await apiClient.delete(`/board/posts/${id}`);
}

// ─── FAQ ───────────────────────────────────────────────────

export interface FaqDto {
  id: string;
  category: string;
  question: string;
  answer: string;
  helpfulCount: number;
  notHelpfulCount: number;
  sortOrder: number;
}

export async function listFaqs(category?: string, q?: string): Promise<FaqDto[]> {
  const params: Record<string, string> = {};
  if (category) params.category = category;
  if (q) params.q = q;
  const { data } = await apiClient.get<{ faqs: FaqDto[] }>('/board/faq', { params });
  return Array.isArray(data?.faqs) ? data.faqs : [];
}

export async function postFaqFeedback(id: string, helpful: boolean): Promise<FaqDto> {
  const { data } = await apiClient.post<{ faq: FaqDto }>(`/board/faq/${id}/feedback`, { helpful });
  return data.faq;
}

// ─── FAQ admin (board v2) ──────────────────────────────────

export interface CreateFaqInput {
  category: string;
  question: string;
  answer: string;
  sortOrder?: number;
  categoryId?: string | null;
}

export interface UpdateFaqInput {
  category?: string;
  question?: string;
  answer?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export async function createFaq(body: CreateFaqInput): Promise<FaqDto> {
  const { data } = await apiClient.post<{ faq: FaqDto }>('/board/faq', body);
  return data.faq;
}

export async function updateFaq(id: string, body: UpdateFaqInput): Promise<FaqDto> {
  const { data } = await apiClient.patch<{ faq: FaqDto }>(`/board/faq/${id}`, body);
  return data.faq;
}

export async function deleteFaq(id: string): Promise<void> {
  await apiClient.delete(`/board/faq/${id}`);
}

// ─── Inquiry ───────────────────────────────────────────────

export type InquiryStatus = 'PENDING' | 'ANSWERED';

export interface InquiryDto {
  id: string;
  category: string;
  title: string;
  content: string;
  status: InquiryStatus;
  answer: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
  createdAt: string;
}

export interface CreateInquiryInput {
  category: string;
  title: string;
  content: string;
}

export async function listInquiries(): Promise<InquiryDto[]> {
  const { data } = await apiClient.get<{ inquiries: InquiryDto[] }>('/board/inquiries');
  return Array.isArray(data?.inquiries) ? data.inquiries : [];
}

export async function getInquiry(id: string): Promise<InquiryDto> {
  const { data } = await apiClient.get<{ inquiry: InquiryDto }>(`/board/inquiries/${id}`);
  return data.inquiry;
}

export async function createInquiry(input: CreateInquiryInput): Promise<InquiryDto> {
  const { data } = await apiClient.post<{ inquiry: InquiryDto }>('/board/inquiries', input);
  return data.inquiry;
}

export async function answerInquiry(id: string, answer: string): Promise<InquiryDto> {
  const { data } = await apiClient.patch<{ inquiry: InquiryDto }>(`/board/inquiries/${id}/answer`, { answer });
  return data.inquiry;
}
